import { useCallback, useEffect, useState } from "react";
import { CircleNotch, CaretLeft, CaretRight, Plus, WarningCircle } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const money = (v) => (v === null || v === undefined ? "—" : `$${Number(v).toLocaleString("en-US", { minimumFraction: 2, maximumFraction: 2 })}`);
// Pay periods run Saturday → Friday (owner 2026-09-05), payday the Friday after.
const saturday = (d) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 1) % 7)); return x.toISOString().slice(0, 10); };
const defaultReconcileWeek = () => {
  // The period you reconcile today = the one whose payday (Friday) just passed
  const today = new Date();
  const day = today.getDay();
  const lastFriday = new Date(today); lastFriday.setDate(today.getDate() - ((day - 5 + 7) % 7));
  const periodEnd = new Date(lastFriday); periodEnd.setDate(lastFriday.getDate() - 7);
  const periodStart = new Date(periodEnd); periodStart.setDate(periodEnd.getDate() - 6);
  return periodStart.toISOString().slice(0, 10);
};

export default function ProfitabilityPage() {
  const [week, setWeek] = useState(defaultReconcileWeek);
  const [data, setData] = useState(null);
  const [clients, setClients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [addRow, setAddRow] = useState({ client_id: "", user_id: "", hours: "", bill_rate: "", pay_rate: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setData(null);
    api.get("/admin/profitability", { params: { week } }).then((r) => setData(r.data))
      .catch((e) => { toast.error(formatApiError(e)); setData({ rows: [], totals: {} }); });
  }, [week]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data.clients || r.data || [])).catch(() => {});
    api.get("/users").then((r) => setStaff((r.data.users || r.data || []).filter((u) => u.role !== "admin"))).catch(() => {});
  }, []);

  const shiftWeek = (days) => {
    const d = new Date(week); d.setDate(d.getDate() + days); setWeek(d.toISOString().slice(0, 10));
  };

  const saveCell = async (row, field, value) => {
    try {
      await api.put("/admin/profitability/row", {
        week, client_id: row.client_id, user_id: row.user_id,
        client_name: row.client_name, caregiver_name: row.caregiver_name, [field]: value,
      });
      toast.success("Saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addManual = async () => {
    if (!addRow.client_id || !addRow.user_id || !addRow.hours) { toast.error("Pick a client, a caregiver, and hours"); return; }
    setBusy(true);
    try {
      const c = clients.find((x) => (x.id || x._id) === addRow.client_id) || {};
      const s = staff.find((x) => (x.id || x._id) === addRow.user_id) || {};
      await api.put("/admin/profitability/row", {
        week, client_id: addRow.client_id, user_id: addRow.user_id,
        client_name: (c.forms?.intake?.full_name) || c.name || "", caregiver_name: s.name || "",
        hours: addRow.hours, bill_rate: addRow.bill_rate, pay_rate: addRow.pay_rate, manual: true,
      });
      toast.success("Row added");
      setAddRow({ client_id: "", user_id: "", hours: "", bill_rate: "", pay_rate: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const t = data?.totals || {};

  return (
    <div className="mx-auto max-w-6xl px-4 py-8" data-testid="profitability-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl tracking-tight text-[#4A235A]">Billing · payroll · profitability</h1>
          <p className="mt-1 text-sm text-stone-500">
            Known statics do the math: client billing rates × caregiver pay rates × hours from visits.
            Anything wrong or missing is editable right in the row — entries save for the week.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => shiftWeek(-7)} className="rounded-full border border-stone-300 p-2 hover:bg-stone-50" data-testid="week-prev"><CaretLeft size={14} /></button>
          <input type="date" value={week} onChange={(e) => setWeek(saturday(new Date(e.target.value)))}
            className="rounded-lg border border-stone-300 px-3 py-1.5 text-sm" data-testid="week-picker" />
          <button onClick={() => shiftWeek(7)} className="rounded-full border border-stone-300 p-2 hover:bg-stone-50" data-testid="week-next"><CaretRight size={14} /></button>
        </div>
      </div>

      <div className="mt-5 rounded-xl border border-[#4A235A]/20 bg-[#4A235A]/[0.03] p-5" data-testid="cycle-explainer">
        <p className="text-xs font-semibold uppercase tracking-wider text-[#4A235A]">How the money cycle works</p>
        <div className="mt-2 grid gap-2 text-sm text-stone-600 sm:grid-cols-2">
          <p><strong>Pay period:</strong> Saturday through Friday. Everyone is paid the <strong>following Friday</strong>.</p>
          <p><strong>On-demand pay:</strong> some people don't wait — they take pay early (by the day, for the $1 fee). When the period's payroll is done, those early payments reconcile back to the period the hours belong to, no matter when the money went out.</p>
          <p><strong>Billing:</strong> we bill the insurance company for a period right after it ends; the money lands about Wednesday.</p>
          <p><strong>Reconciling:</strong> a period can only be reconciled once its payroll has concluded — so on any given week you're reconciling the period whose payday just passed (last Friday), not the one that just ended.</p>
        </div>
      </div>

      {!data ? (
        <div className="flex justify-center py-20"><CircleNotch size={28} className="animate-spin text-primary" /></div>
      ) : (
        <>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-stone-400" data-testid="week-label">
              Pay period {data.week} – {data.week_end} · paid out {data.pay_date}
            </p>
            <button
              onClick={async () => {
                try {
                  await api.put("/admin/profitability/reconcile", { week: data.week, reconciled: !data.reconciled });
                  toast.success(data.reconciled ? "Period reopened" : "Period reconciled — everything for it ties out");
                  load();
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              className={`rounded-full px-4 py-1 text-xs font-semibold transition-colors ${data.reconciled ? "border border-emerald-300 bg-emerald-50 text-emerald-700" : "bg-[#4A235A] text-white hover:bg-stone-900"}`}
              data-testid="reconcile-toggle">
              {data.reconciled ? `✓ Reconciled ${String(data.reconciled_at || "").slice(0, 10)} by ${data.reconciled_by} — click to reopen` : "Mark this period reconciled"}
            </button>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              ["Revenue (billed)", money(t.revenue), "text-[#2F6B4F]", "tot-revenue"],
              ["Payroll (labor)", money(t.cost), "text-stone-700", "tot-cost"],
              ["Margin", money(t.margin), (t.margin || 0) >= 0 ? "text-[#2F6B4F]" : "text-[#C0392B]", "tot-margin"],
              ["Margin %", t.margin_pct === null || t.margin_pct === undefined ? "—" : `${t.margin_pct}%`, "text-[#4A235A]", "tot-margin-pct"],
            ].map(([label, value, cls, tid]) => (
              <div key={tid} className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm" data-testid={tid}>
                <p className={`font-heading text-2xl font-semibold ${cls}`}>{value}</p>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400">{label}</p>
              </div>
            ))}
          </div>
          {(t.missing_rates || 0) > 0 && (
            <p className="mt-2 flex items-center gap-1.5 text-xs text-[#8a5d14]" data-testid="missing-rates-note">
              <WarningCircle size={14} /> {t.missing_rates} row(s) are missing a billing or pay rate — fill it in below.
            </p>
          )}

          <div className="mt-4 overflow-x-auto rounded-xl border border-stone-200 bg-white shadow-sm">
            <table className="w-full text-sm" data-testid="profitability-table">
              <thead>
                <tr className="border-b border-stone-200 text-left text-[11px] uppercase tracking-wider text-stone-400">
                  <th className="px-3 py-2">Client</th>
                  <th className="px-3 py-2">Caregiver</th>
                  <th className="px-3 py-2 text-right">Visits</th>
                  <th className="px-3 py-2 text-right">Hours</th>
                  <th className="px-3 py-2 text-right">Bill $/hr</th>
                  <th className="px-3 py-2 text-right">Pay $/hr</th>
                  <th className="px-3 py-2 text-right">Revenue</th>
                  <th className="px-3 py-2 text-right">Cost</th>
                  <th className="px-3 py-2 text-right">Margin</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, i) => (
                  <tr key={`${r.client_id}-${r.user_id}-${i}`} className="border-b border-stone-100 last:border-0" data-testid={`profit-row-${i}`}>
                    <td className="px-3 py-2 font-medium text-stone-800">{r.client_name}{r.manual && <span className="ml-1 rounded bg-stone-100 px-1 text-[9px] font-bold uppercase text-stone-400">manual</span>}</td>
                    <td className="px-3 py-2 text-stone-600">{r.caregiver_name}</td>
                    <td className="px-3 py-2 text-right text-stone-500">{r.visits || "—"}</td>
                    <td className="px-3 py-2 text-right">
                      <input type="number" step="0.25" key={`h-${r.client_id}-${r.user_id}-${r.hours}`} defaultValue={r.hours}
                        onBlur={(e) => { const v = parseFloat(e.target.value); if (v && v !== r.hours) saveCell(r, "hours", v); }}
                        className="w-16 rounded border border-stone-200 px-1 py-0.5 text-right text-xs" data-testid={`profit-hours-${i}`} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <input type="number" step="0.01" key={`b-${r.client_id}-${r.user_id}-${r.bill_rate}`} defaultValue={r.bill_rate ?? ""} placeholder="—"
                        onBlur={(e) => { const v = e.target.value === "" ? null : parseFloat(e.target.value); if (v !== r.bill_rate) saveCell(r, "bill_rate", v); }}
                        className="w-16 rounded border border-stone-200 px-1 py-0.5 text-right text-xs" data-testid={`profit-bill-${i}`} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <input type="number" step="0.01" key={`p-${r.client_id}-${r.user_id}-${r.pay_rate}`} defaultValue={r.pay_rate ?? ""} placeholder="—"
                        onBlur={(e) => { const v = e.target.value === "" ? null : parseFloat(e.target.value); if (v !== r.pay_rate) saveCell(r, "pay_rate", v); }}
                        className="w-16 rounded border border-stone-200 px-1 py-0.5 text-right text-xs" data-testid={`profit-pay-${i}`} />
                    </td>
                    <td className="px-3 py-2 text-right text-stone-700">{money(r.revenue)}</td>
                    <td className="px-3 py-2 text-right text-stone-700">{money(r.cost)}</td>
                    <td className={`px-3 py-2 text-right font-semibold ${r.margin === null ? "text-stone-400" : r.margin >= 0 ? "text-[#2F6B4F]" : "text-[#C0392B]"}`}
                      data-testid={`profit-margin-${i}`}>
                      {money(r.margin)}{r.overridden && <span className="ml-1 rounded bg-[#B7791F]/15 px-1 text-[9px] font-bold uppercase text-[#8a5d14]">edited</span>}
                    </td>
                  </tr>
                ))}
                {data.rows.length === 0 && (
                  <tr><td colSpan={9} className="px-3 py-8 text-center text-sm text-stone-400" data-testid="profit-empty">
                    No worked visits this week — add rows manually below, or pick another week.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4 rounded-xl border border-dashed border-stone-300 bg-white p-4" data-testid="profit-add-row">
            <p className="text-xs font-semibold uppercase tracking-wider text-stone-400">Add a row (data entry)</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <select value={addRow.client_id} onChange={(e) => setAddRow({ ...addRow, client_id: e.target.value })}
                className="h-9 rounded-lg border border-stone-300 bg-white px-2 text-xs" data-testid="profit-add-client">
                <option value="">Client…</option>
                {clients.map((c) => <option key={c.id || c._id} value={c.id || c._id}>{c.forms?.intake?.full_name || c.name}</option>)}
              </select>
              <select value={addRow.user_id} onChange={(e) => setAddRow({ ...addRow, user_id: e.target.value })}
                className="h-9 rounded-lg border border-stone-300 bg-white px-2 text-xs" data-testid="profit-add-staff">
                <option value="">Caregiver…</option>
                {staff.map((s) => <option key={s.id || s._id} value={s.id || s._id}>{s.name}</option>)}
              </select>
              <input type="number" step="0.25" placeholder="hours" value={addRow.hours} onChange={(e) => setAddRow({ ...addRow, hours: e.target.value })}
                className="h-9 w-20 rounded-lg border border-stone-300 px-2 text-xs" data-testid="profit-add-hours" />
              <input type="number" step="0.01" placeholder="bill $/hr" value={addRow.bill_rate} onChange={(e) => setAddRow({ ...addRow, bill_rate: e.target.value })}
                className="h-9 w-20 rounded-lg border border-stone-300 px-2 text-xs" data-testid="profit-add-bill" />
              <input type="number" step="0.01" placeholder="pay $/hr" value={addRow.pay_rate} onChange={(e) => setAddRow({ ...addRow, pay_rate: e.target.value })}
                className="h-9 w-20 rounded-lg border border-stone-300 px-2 text-xs" data-testid="profit-add-pay" />
              <button onClick={addManual} disabled={busy}
                className="inline-flex h-9 items-center gap-1 rounded-full bg-[#4A235A] px-4 text-xs font-semibold text-white hover:bg-stone-900 disabled:opacity-50"
                data-testid="profit-add-submit">
                <Plus size={12} weight="bold" /> Add
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
